"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="217528" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-16 bg-muted animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.filter(item => item.label !== "Dining2").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href === "/HomePage" ? "/" : item.href || "#"} className="group relative py-2 text-[10px] lg:text-xs font-default uppercase tracking-widest text-foreground transition-colors hover:text-foreground/80">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[1px] w-full origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute left-0 top-full w-full border-b border-border bg-background p-6 md:hidden shadow-2xl">
      <nav className="flex flex-col gap-6">
        {loading ? <div className="flex flex-col gap-4">
            {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted animate-pulse rounded" />)}
          </div> : menuItems.filter(item => item.label !== "Dining2").map((item, i) => <Link key={item.id} data-index={i} to={item.href === "/HomePage" ? "/" : item.href || "#"} className="text-sm sm:text-base font-default uppercase tracking-widest text-foreground transition-colors hover:text-primary" onClick={onClose}>
              {item.label}
            </Link>)}
        <div className="pt-4 border-t border-border">
          <Button asChild className="w-full border border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-500 text-[10px] sm:text-xs font-default uppercase tracking-widest py-6 rounded-md">
            <Link to="#">Book Your Camp</Link>
          </Button>
        </div>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-700 ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-md" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-6 lg:px-12 h-20 lg:h-24 flex items-center justify-between">
        <Link to="/" className="flex-shrink-0 z-10">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633409/img/the_one_oman_luxury_private_camps_logo.png" alt="The One Oman Private Camps" className="h-7 w-auto object-contain" />
        </Link>

        <MenuProvider menu_id="46" className="flex-1 flex justify-center">
          <DesktopNav />
        </MenuProvider>

        <div className="flex-shrink-0 hidden md:flex z-10">
          <Button asChild className="border border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-500 px-8 py-5 text-xs lg:text-sm font-default uppercase tracking-widest rounded-md">
            
          </Button>
        </div>

        <Button variant="ghost" size="icon" className="md:hidden z-10 text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle mobile menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>

        <MenuProvider menu_id="46">
          <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </section>;
}